import React, { FC, memo, useEffect, useState } from 'react';
import { DivWrapper } from './styles';
import { Canvas } from './utils/canvas';
import { TArt, TArtChangeEvent } from '@features/DesignStudio/redux/types';

interface Props {
  blankUri: string;
  templateUri: string;
  arts: { uri: string; x: number; y: number; w: number; h: number; a: number; actionId: string }[];
  texts: {
    fontSize: number;
    text: string;
    x: number;
    y: number;
    w: number;
    h: number;
    fontFamily: string;
    fontColor: string;
  }[];
  area: { x: number; y: number; w: number; h: number; previewHeight: number };
  // @ts-ignore
  onCanvasInit: (canvas: Canvas) => void;
  onArtworkChange: (art: TArt, index: number) => void;
  onTextChange: (art: TArt, index: number) => void;
}

const FnArtworkCanvas: FC<Props> = ({
  blankUri,
  templateUri,
  arts,
  texts,
  area,
  onCanvasInit,
  onArtworkChange,
  onTextChange,
}) => {
  // @ts-ignore
  const [canvas, setCanvas] = useState<Canvas>();
  const [prevBlank, setPrevBlank] = useState<string>(null);
  const [prevArea, setPrevArea] = useState<Props['area']>(null);
  const [prevArts, setPrevArts] = useState<Props['arts']>(arts);
  const [prevTexts, setPrevTexts] = useState<Props['texts']>(texts);

  useEffect(() => {
    initCanvas();
  }, []);

  // art handler
  useEffect(() => {
    if (!canvas) {
      return;
    }

    if (prevArts?.length === arts?.length && prevArts?.every((prevArt, index) => (
      prevArt?.uri === arts[index]?.uri && prevArt?.a === arts[index]?.a
      && prevArt?.x === arts[index]?.x && prevArt?.y === arts[index]?.y
      && prevArt?.w === arts[index]?.w && prevArt?.h === arts[index]?.h
      && prevArt?.actionId === arts[index]?.actionId
    ))) {
      return;
    }

    if (!arts?.length) {
      canvas._removeArt();
      setPrevArts(null);

      return;
    }

    setPrevArts(arts);
    canvas.addArts(arts);
  }, [arts, canvas, prevArts]);

  // text handler
  useEffect(() => {
    if (!canvas?.$Blank) {
      return;
    }

    if (prevTexts?.length === texts?.length && prevTexts?.every((prevText, index) => (
      texts[index].x === prevText?.x && texts[index].y === prevText?.y &&
      texts[index].w === prevText?.w && texts[index].h === prevText?.h &&
      texts[index].text === prevText?.text && texts[index].fontSize === prevText?.fontSize &&
      texts[index].fontFamily === prevText?.fontFamily && texts[index].fontColor === prevText?.fontColor
    ))) {
      return;
    }

    setPrevTexts(texts);
    canvas.redrawText(texts);
  }, [texts, canvas, prevTexts]);

  // area handler
  useEffect(() => {
    if (!area || !canvas?.$Blank ||
      area.x === prevArea?.x && area.y === prevArea?.y &&
      area.w === prevArea?.w && area.h === prevArea?.h) {
      return;
    }

    setPrevArea(area);
    canvas.redrawArea(area);
  }, [area, canvas, prevArea]);

  // blank handler
  useEffect(() => {
    if (!blankUri || !canvas?.$Blank || prevBlank === blankUri) {
      return;
    }

    setPrevBlank(blankUri);
    canvas.redrawBlank(blankUri, templateUri);
  }, [blankUri, canvas, prevBlank, templateUri]);

  const initCanvas = () => {
    const data = {
      canvasID: 'canvas_svg',
      blankUri,
      templateUri,
      arts,
      area,
      texts,
      onChange: (art: TArtChangeEvent, index: number) => {
        if (!art) {
          canvas?._redraw(canvas);
          return;
        }

        onArtworkChange(art, index);
      },
      onTextChange: (text: TArtChangeEvent, index: number) => {
        if (!text) {
          canvas?._redraw(canvas);
          return;
        }

        onTextChange(text, index);
      },
    };

    const canvasInstance = new Canvas(data);
    canvasInstance.create().finally(() => {
      canvasInstance._redraw(canvasInstance);
      setPrevBlank(blankUri);
      setPrevArts(arts);
      setPrevArea(area);
    });

    setCanvas(canvasInstance);
    onCanvasInit(canvasInstance);
  };

  return (
    <DivWrapper>
      <div>
        <svg
          id="canvas_svg"
          style={{ display: 'inline' }}
          width="100%"
          height="100%"
        />
      </div>
    </DivWrapper>
  );
};

export default memo(FnArtworkCanvas);
